import { DatePipe, DecimalPipe, NgClass, PercentPipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatCard, MatCardContent, MatCardHeader } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DxSummaryLineItem } from '../../../models/dx-summary-line-item';
import { ReportNames } from '../../../models/report-names';
import { CalculationsService } from '../../../services/calculations/calculations.service';
import { PermissionMap, PermissionService } from '../../../services/settings/permission.service';
import { SettingsService } from '../../../services/settings/settings.service';

interface DxSummaryWidgetRow {
  key: string;
  label: string;
  hccCount: number;
  dxCount: number;
  level: number;
  expandable: boolean;
  children: DxSummaryWidgetRow[];
}

@Component({
  selector: 'app-dx-events-summary-widget',
  templateUrl: './dx-events-summary-widget.component.html',
  styleUrls: ['./dx-events-summary-widget.component.scss'],
  standalone: true,
  imports: [MatCard, MatCardHeader, RouterLink, MatTooltip, MatCardContent, MatIcon, MatSlideToggle, FormsModule, NgClass, DecimalPipe, PercentPipe, DatePipe],
})
export class DxEventsSummaryWidgetComponent implements OnInit, OnDestroy {

  @Input() queryData = true;

  summaryDxSwitch = false;
  serviceYear = -1;
  reportingEnabled = false;
  loading = false;
  refreshDate: Date | null = null;
  summaryRows: DxSummaryWidgetRow[] = [];
  visibleRows: DxSummaryWidgetRow[] = [];

  readonly DIAGNOSIS_EVENTS_SUMMARY_NAME = ReportNames.DIAGNOSIS_EVENTS_SUMMARY_NAME;

  private readonly expandedKeys = new Set<string>();
  private readonly subs: Subscription[] = [];
  private dataSub?: Subscription;

  constructor(
    private settingsService: SettingsService,
    private calculationsService: CalculationsService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.subs.push(this.settingsService.serviceYear.subscribe(result => {
      if (result > -1) {
        this.serviceYear = result;

        if (this.queryData) {
          this.loadSummaryData();
        }
      }
    }));

    this.subs.push(this.permissionService.activeUserPermissions.subscribe(result => {
      this.reportingEnabled = result.some(permission => permission.PermissionID === PermissionMap.GenerateReportsDxEvents);
    }));

    this.subs.push(this.calculationsService.lastRefreshDate.subscribe(result => {
      this.refreshDate = result;
    }));
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.subs.forEach(subscription => subscription.unsubscribe());
  }

  toggleRow(row: DxSummaryWidgetRow): void {
    if (!this.canToggleRow(row)) {
      return;
    }

    if (this.expandedKeys.has(row.key)) {
      this.expandedKeys.delete(row.key);
    }
    else {
      this.expandedKeys.add(row.key);
    }

    this.visibleRows = this.flattenRows(this.summaryRows);
  }

  isExpanded(row: DxSummaryWidgetRow): boolean {
    return this.expandedKeys.has(row.key);
  }

  canToggleRow(row: DxSummaryWidgetRow): boolean {
    return row.level === 2 && row.expandable;
  }

  getLabelPadding(row: DxSummaryWidgetRow): number {
    if (row.level <= 1) {
      return 0;
    }

    if (row.level === 3) {
      return 44;
    }

    return (row.level - 1) * 20;
  }

  getSelectedCount(row: DxSummaryWidgetRow): number {
    return this.summaryDxSwitch ? row.dxCount : row.hccCount;
  }

  getSelectedPercent(row: DxSummaryWidgetRow): number | null {
    if (row.level === 0) {
      return null;
    }

    const total = this.summaryRows[0] == null
      ? 0
      : (this.summaryDxSwitch ? this.summaryRows[0].dxCount : this.summaryRows[0].hccCount);

    if (total === 0) {
      return 0;
    }

    return this.getSelectedCount(row) / total;
  }

  isOpenStateRow(row: DxSummaryWidgetRow): boolean {
    return row.level === 1 && row.label.trim().toLowerCase() === 'open';
  }

  private loadSummaryData(): void {
    this.loading = true;
    this.dataSub?.unsubscribe();

    this.dataSub = this.calculationsService.getDxSummary(this.serviceYear).subscribe({
      next: result => {
        this.summaryRows = this.buildSummaryRows(result ?? []);
        this.visibleRows = this.flattenRows(this.summaryRows);
        this.loading = false;
      },
      error: () => {
        this.summaryRows = this.buildSummaryRows([]);
        this.visibleRows = this.flattenRows(this.summaryRows);
        this.loading = false;
      }
    });
  }

  private buildSummaryRows(items: DxSummaryLineItem[]): DxSummaryWidgetRow[] {
    const totalRow: DxSummaryWidgetRow = {
      key: 'total',
      label: 'Total',
      ...this.getTotals(items),
      level: 0,
      expandable: false,
      children: []
    };

    const stateRows = this.groupBy(items, item => item.State).map(([state, stateItems]) => {
      const suppressRootCauseRows = this.isRootCauseSuppressedState(state);
      const statusRows = this.groupBy(stateItems, item => item.Status).map(([status, statusItems]) => {
        const rootCauseRows = suppressRootCauseRows
          ? []
          : this.groupBy(statusItems, item => item.RootCause).map(([rootCause, rootCauseItems]) => ({
          key: `root-cause:${state}|${status}|${rootCause}`,
          label: this.getDisplayLabel(rootCause, 'Unspecified Root Cause'),
          ...this.getTotals(rootCauseItems),
          level: 3,
          expandable: false,
          children: []
        }));

        return {
          key: `status:${state}|${status}`,
          label: this.getDisplayLabel(status, 'Unspecified Status'),
          ...this.getTotals(statusItems),
          level: 2,
          expandable: rootCauseRows.length > 0,
          children: rootCauseRows
        };
      });

      return {
        key: `state:${state}`,
        label: this.getDisplayLabel(state, 'Unspecified State'),
        ...this.getTotals(stateItems),
        level: 1,
        expandable: false,
        children: statusRows
      };
    });

    return [totalRow, ...stateRows];
  }

  private flattenRows(rows: DxSummaryWidgetRow[]): DxSummaryWidgetRow[] {
    const flattenedRows: DxSummaryWidgetRow[] = [];

    rows.forEach(row => {
      flattenedRows.push(row);

      if (row.level === 1) {
        flattenedRows.push(...this.flattenRows(row.children));
      }
      else if (row.expandable && this.expandedKeys.has(row.key)) {
        flattenedRows.push(...this.flattenRows(row.children));
      }
    });

    return flattenedRows;
  }

  private groupBy(items: DxSummaryLineItem[], selector: (item: DxSummaryLineItem) => string): [string, DxSummaryLineItem[]][] {
    const groups = new Map<string, DxSummaryLineItem[]>();

    items.forEach(item => {
      const key = selector(item);
      const existingItems = groups.get(key);

      if (existingItems == null) {
        groups.set(key, [item]);
        return;
      }

      existingItems.push(item);
    });

    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }

  private getTotals(items: DxSummaryLineItem[]): Pick<DxSummaryWidgetRow, 'hccCount' | 'dxCount'> {
    return items.reduce((totals, item) => {
      totals.hccCount += item.HCCCount;
      totals.dxCount += item.DxCount;
      return totals;
    }, { hccCount: 0, dxCount: 0 });
  }

  private getDisplayLabel(value: string, fallback: string): string {
    const trimmedValue = value?.trim();
    return trimmedValue ? trimmedValue : fallback;
  }

  private isOpenStateValue(value: string): boolean {
    return value?.trim().toLowerCase() === 'open';
  }

  private isPendingStateValue(value: string): boolean {
    return value?.trim().toLowerCase() === 'pending';
  }

  private isRootCauseSuppressedState(value: string): boolean {
    return this.isOpenStateValue(value) || this.isPendingStateValue(value);
  }
}
